import React from "react";
import PropTypes from "prop-types";
import styles from "./PeopleSidebar.scss";
import { Sidebar } from "../sidebar/Sidebar";
import { CloseButton } from "../input/CloseButton";
import { IconButton } from "../input/IconButton";
import { ReactComponent as MicrophoneFilledIcon } from "../icons/MicrophoneFilled.svg";
import { ReactComponent as MicrophoneMutedIcon } from "../icons/MicrophoneMuted.svg";
import { ReactComponent as HandRaisedIcon } from "../icons/HandRaised.svg";
import { List, ButtonListItem } from "../layout/List";
import { FormattedMessage, useIntl } from "react-intl";
import { FirstPersonAvatarPreview } from "./FirstPersonAvatarPreview";
import { ReactComponent as DesktopIcon } from "../icons/Desktop.svg";
import { ReactComponent as PhoneIcon } from "../icons/Phone.svg";
import { ReactComponent as VRIcon } from "../icons/VR.svg";
import { ReactComponent as DiscordIcon } from "../icons/Discord.svg";
import { ReactComponent as StarIcon } from "../icons/Star.svg";
import { getPeopleSidebarVoiceIconKey, PEOPLE_SIDEBAR_VOICE_ICON } from "./people-sidebar-utils";

export function getVoiceLabel(micPresence, intl) {
  if (micPresence) {
    if (micPresence.muted) {
      return intl.formatMessage({ id: "people-sidebar.voice-label.muted", defaultMessage: "Muted" });
    }

    return intl.formatMessage({ id: "people-sidebar.voice-label.live", defaultMessage: "Live" });
  }

  return intl.formatMessage({ id: "people-sidebar.voice-label.not-talking", defaultMessage: "Not Talking" });
}

export function getVoiceIconComponent(micPresence) {
  if (getPeopleSidebarVoiceIconKey(micPresence) === PEOPLE_SIDEBAR_VOICE_ICON.LIVE) {
    return MicrophoneFilledIcon;
  }

  return MicrophoneMutedIcon;
}

import { PermissionNotification } from "./PermissionNotifications";

function getDeviceLabel(ctx, intl) {
  if (ctx) {
    if (ctx.hmd) {
      return intl.formatMessage({ id: "people-sidebar.device-label.vr", defaultMessage: "VR" });
    } else if (ctx.discord) {
      return intl.formatMessage({ id: "people-sidebar.device-label.discord", defaultMessage: "Discord Bot" });
    } else if (ctx.mobile) {
      return intl.formatMessage({ id: "people-sidebar.device-label.mobile", defaultMessage: "Mobile" });
    }
  }

  return intl.formatMessage({ id: "people-sidebar.device-label.desktop", defaultMessage: "Desktop" });
}

function getDeviceIconComponent(ctx) {
  if (ctx) {
    if (ctx.hmd) {
      return VRIcon;
    } else if (ctx.discord) {
      return DiscordIcon;
    } else if (ctx.mobile) {
      return PhoneIcon;
    }
  }

  return DesktopIcon;
}

function getPresenceMessage(presence, intl) {
  switch (presence) {
    case "lobby":
      return intl.formatMessage({ id: "people-sidebar.presence.in-lobby", defaultMessage: "In Lobby" });
    case "room":
      return intl.formatMessage({ id: "people-sidebar.presence.in-room", defaultMessage: "In Verse" });
    case "entering":
      return intl.formatMessage({ id: "people-sidebar.presence.entering", defaultMessage: "Entering Verse" });
    default:
      return undefined;
  }
}

function getPersonName(person, intl) {
  const you = intl.formatMessage({
    id: "people-sidebar.person-name.you",
    defaultMessage: "You"
  });

  return person.profile.displayName + (person.isMe ? ` (${you})` : "");
}

export function PeopleSidebar({
  people,
  onSelectPerson,
  onClose,
  showMuteAll,
  onMuteAll,
  canVoiceChat,
  voiceChatEnabled,
  isMod
}) {
  const intl = useIntl();

  const userOwner = people.filter(person => person.roles.owner);
  const guestPeople = people.filter(person => !person.roles.owner);
  const me = people.find(person => !!person.isMe);
  const filteredPeople = people.filter(person => !person.isMe);
  me && filteredPeople.unshift(me);

  return (
    <Sidebar
      title={
        <FormattedMessage
          id="people-sidebar.title"
          defaultMessage="Participants ({numPeople})"
          values={{ numPeople: people.length }}
        />
      }
      afterTitle={<CloseButton onClick={onClose} />}
    >
      {!canVoiceChat && <PermissionNotification permission={"voice_chat"} />}
      {!voiceChatEnabled && isMod && <PermissionNotification permission={"voice_chat"} isMod={true} />}
      {/* Hosts */}
      {userOwner.length !== 0 && (
        <div>
          <p className={styles.role}>
            <FormattedMessage
              id="people-sidebar.host"
              defaultMessage="Host ({numPeople})"
              values={{ numPeople: userOwner.length }}
            />
            {showMuteAll ? (
              <IconButton onClick={onMuteAll}>
                <FormattedMessage
                  className={styles.role}
                  id="people-sidebar.mute-all-button"
                  defaultMessage="Mute All"
                />
              </IconButton>
            ) : undefined}
          </p>
          <List>
            {userOwner.map(person => {
              const VoiceIcon = getVoiceIconComponent(person.micPresence);
              const DeviceIcon = getDeviceIconComponent(person.context);

              return (
                <ButtonListItem
                  className={styles.person}
                  key={person.id}
                  type="button"
                  onClick={e => onSelectPerson(person, e)}
                >
                  {<DeviceIcon className={styles.deviceColor} title={getDeviceLabel(person.context, intl)} />}
                  <FirstPersonAvatarPreview
                    signedIn={true}
                    isAbsolute={false}
                    showContextualSignInDialog={() => {}}
                    profile={person.profile}
                    customStyle={styles.avatarHeight}
                  ></FirstPersonAvatarPreview>

                  {person.hand_raised && <HandRaisedIcon />}

                  <p className={styles.personName}>{getPersonName(person, intl)}</p>

                  {person.roles.owner && (
                    <StarIcon
                      title={intl.formatMessage({ id: "people-sidebar.moderator-label", defaultMessage: "Moderator" })}
                      className={styles.moderatorIcon}
                      width={12}
                      height={12}
                    />
                  )}
                  <p className={styles.spaceInfo}>{getPresenceMessage(person.presence, intl)}</p>

                  {!person.context.discord && VoiceIcon && (
                    <VoiceIcon title={getVoiceLabel(person.micPresence, intl)} />
                  )}
                </ButtonListItem>
              );
            })}
          </List>
        </div>
      )}

      {/* Guests */}
      {guestPeople.length !== 0 && (
        <div>
          <p className={styles.role}>
            <FormattedMessage
              id="people-sidebar.guests"
              defaultMessage="Guests ({numPeople})"
              values={{ numPeople: guestPeople.length }}
            />
          </p>
          <List>
            {guestPeople.map(person => {
              const VoiceIcon = getVoiceIconComponent(person.micPresence);
              const DeviceIcon = getDeviceIconComponent(person.context);

              return (
                <ButtonListItem
                  className={styles.person}
                  key={person.id}
                  type="button"
                  onClick={e => onSelectPerson(person, e)}
                >
                  {<DeviceIcon className={styles.deviceColor} title={getDeviceLabel(person.context, intl)} />}
                  <FirstPersonAvatarPreview
                    signedIn={true}
                    isAbsolute={false}
                    showContextualSignInDialog={() => {}}
                    profile={person.profile}
                    customStyle={styles.avatarHeight}
                  ></FirstPersonAvatarPreview>

                  {person.hand_raised && <HandRaisedIcon />}

                  <p className={styles.personName}>{getPersonName(person, intl)}</p>

                  {person.roles.owner && (
                    <StarIcon
                      title={intl.formatMessage({ id: "people-sidebar.moderator-label", defaultMessage: "Moderator" })}
                      className={styles.moderatorIcon}
                      width={12}
                      height={12}
                    />
                  )}
                  <p className={styles.spaceInfo}>{getPresenceMessage(person.presence, intl)}</p>

                  {!person.context.discord && VoiceIcon && (
                    <VoiceIcon title={getVoiceLabel(person.micPresence, intl)} />
                  )}
                </ButtonListItem>
              );
            })}
          </List>
        </div>
      )}
    </Sidebar>
  );
}

PeopleSidebar.propTypes = {
  people: PropTypes.array,
  onSelectPerson: PropTypes.func,
  showMuteAll: PropTypes.bool,
  onMuteAll: PropTypes.func,
  onClose: PropTypes.func,
  canVoiceChat: PropTypes.bool,
  voiceChatEnabled: PropTypes.bool,
  isMod: PropTypes.bool
};

PeopleSidebar.defaultProps = {
  people: [],
  onSelectPerson: () => {},
  isMod: false
};
